/* eslint-disable react/forbid-elements */
import React from 'react';

import find from 'lodash/find';

import { useClassnames } from '@lumapps/classnames';
import { get } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { sendTrackingDirectoryEntryClickActionEvent } from '@lumapps/directories/api/analytics';
import { getDirectoryById } from '@lumapps/directories/ducks/selectors';
import { DirectoryTemplateComponentType } from '@lumapps/directories/types';
import { ConfirmDialog } from '@lumapps/lumx-dialogs/components/ConfirmDialog';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { ContextMenu } from '@lumapps/lumx-menu/components/ContextMenu';
import { MenuOption, MenuOptionType } from '@lumapps/lumx-menu/components/ContextMenu/types';
import { ColorBubbleLetter } from '@lumapps/lumx-texts/components/ColorBubbleLetter';
import { mdiEmailOutline, mdiLink, mdiPhone, mdiStar, mdiStarOutline } from '@lumapps/lumx/icons';
import {
    AspectRatio,
    ColorPalette,
    ColorVariant,
    Emphasis,
    IconButton,
    Link,
    ThumbnailVariant,
    Theme,
    Text,
    Typography,
    Thumbnail,
    Badge,
    Icon,
    Heading,
    HeadingElement,
} from '@lumapps/lumx/react';
import { MetadataLinkList } from '@lumapps/metadata-link/components/MetadataLinkList';
import { MetadataListV2 } from '@lumapps/metadata/types';
import { MicroAppDialog } from '@lumapps/micro-apps/components/MicroAppDialog';
import { sendWebviewMobileMessage } from '@lumapps/mobile-webview/message-emit';
import { useSelector } from '@lumapps/redux/react';
import { isExternalUrl } from '@lumapps/router/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { hashCode } from '@lumapps/utils/string/hashCode';

import { ENTRY_TYPES } from '../../constants';
import { useEntryLink } from '../../hooks/useEntryLink';
import { DIRECTORY_ENTRY } from '../../keys';
import {
    DetailedDirectoryEntryBlockProps,
    DetailedDirectoryEntryVariant,
    FormattedDirectoryEntryField,
} from '../../types';
import { getDirectoryEntryIcon } from '../../utils';
import { DirectoryEntryCreationDialog } from '../DirectoryEntryCreationDialog';

import './index.scss';

const Config = get();

export const CLASSNAME = 'directory-entry-block';

const isWebviewApp = Config.applicationId === AppId.webview;

export const DetailedDirectoryEntryBlock = ({
    additionalFields,
    directoryEntryId,
    directoryEntryType,
    directoryId,
    microAppProperties,
    resourceId,
    title,
    tags,
    metadata,
    link,
    image,
    isFavorite,
    isFavoriteIconVisible,
    variant = DetailedDirectoryEntryVariant.horizontal,
    favorite,
    unfavorite,
    canEdit = false,
    canDelete = false,
    theme = Theme.light,
    creationDialogProps,
    deleteDialogProps,
    openDeleteDialog,
    openSaveDialog,
    badgeProps,
    classname,
    titleAs,
}: DetailedDirectoryEntryBlockProps) => {
    const { translateKey, translateAndReplace } = useTranslate();
    const { element, block } = useClassnames(CLASSNAME);
    const currentLanguage = useSelector(currentLanguageSelector);

    const [isMicroAppDialogOpen, toggleMicroAppDialog] = useBooleanState(false);

    const href = useEntryLink(link) || undefined;

    const directory = useSelector(getDirectoryById(directoryId));

    const formattedAdditionalFields = React.useMemo<FormattedDirectoryEntryField[]>(() => {
        if (!additionalFields) {
            return [];
        }

        return additionalFields.map((field) => {
            // Get field associated template
            const templateComponent = find(directory?.template?.components, { uuid: field.id });

            if (templateComponent?.type === DirectoryTemplateComponentType.url) {
                return { ...field, type: templateComponent.url?.type ?? 'url' };
            }

            return field;
        });
    }, [additionalFields, directory]);

    const linkProps = React.useMemo(
        () =>
            directoryEntryType === ENTRY_TYPES.LINK
                ? {
                      href,
                      target: isExternalUrl(href) ? '_blank' : '_self',
                      rel: isExternalUrl(href) ? 'noopener noreferrer' : undefined,
                  }
                : {},
        [directoryEntryType, href],
    );

    // Metadata needs to be formaded to type MetadataListV2 in order to be used with MetadataLinkList component
    const metadataList: MetadataListV2 | undefined = metadata?.map(({ metadataId, name }) => ({
        name,
        id: metadataId,
    }));

    const trackClickActions = () => {
        if (directoryEntryType === ENTRY_TYPES.MICROAPP && resourceId) {
            if (isWebviewApp) {
                sendWebviewMobileMessage({
                    type: 'redirect',
                    payload: { type: 'unknown', url: link },
                });
            } else {
                toggleMicroAppDialog();
            }
        }
        sendTrackingDirectoryEntryClickActionEvent({
            targetId: directoryEntryId,
            url: link,
            title,
            thumbnail: image?.url,
        });
    };

    const microAppIcon = getDirectoryEntryIcon({ type: directoryEntryType, resourceProperties: microAppProperties });

    const microAppThumbnail = microAppProperties?.componentSettings.length
        ? microAppIcon?.[currentLanguage] || ''
        : undefined;

    const thumbnailUrl = image?.url || microAppThumbnail;

    const menuOptions = React.useMemo(() => {
        const options: MenuOption[] = [];

        if (canEdit && openSaveDialog) {
            options.push({
                type: MenuOptionType.option,
                labelKey: translateKey(GLOBAL.EDIT),
                onSelect: () => {
                    openSaveDialog(directoryEntryId);
                },
            });
        }

        if (canDelete) {
            options.push({
                type: MenuOptionType.option,
                labelKey: translateKey(GLOBAL.DELETE),
                onSelect: openDeleteDialog,
            });
        }

        return options;
    }, [canEdit, canDelete, translateKey, openSaveDialog, directoryEntryId, openDeleteDialog]);

    return (
        <>
            <div
                key={directoryEntryId}
                data-testid="container-directory-entry"
                className={block(
                    {
                        [`theme-${theme}`]: true,
                        horizontal: variant === DetailedDirectoryEntryVariant.horizontal,
                        vertical: variant === DetailedDirectoryEntryVariant.vertical,
                    },
                    classname,
                )}
            >
                {/**
                 * Hide thumbnail from screen readers and remove focus from it as it is only decorative.
                 * As the title itself is the same link, this element is only useful for mouse users that
                 * want to click on the icon instead of the title.
                 */}
                <Link
                    className={element('thumbnail-wrapper')}
                    tabIndex={-1}
                    aria-hidden="true"
                    {...linkProps}
                    onClick={trackClickActions}
                >
                    {thumbnailUrl ? (
                        <Thumbnail
                            {...image}
                            theme={theme}
                            alt=""
                            variant={ThumbnailVariant.squared}
                            aspectRatio={AspectRatio.square}
                            image={thumbnailUrl}
                            className={element('image')}
                            badge={
                                badgeProps && (
                                    <Badge {...badgeProps}>
                                        <Icon icon={badgeProps.icon} />
                                    </Badge>
                                )
                            }
                        />
                    ) : (
                        <ColorBubbleLetter
                            className={element('letter')}
                            text={title || link}
                            colors={Config.defaultColors}
                        />
                    )}
                </Link>

                <div className={element('content')}>
                    <Heading as={titleAs as HeadingElement} typography="custom-title5">
                        <Link {...linkProps} className={element('title')} onClick={trackClickActions}>
                            {title || link}
                        </Link>
                    </Heading>

                    {!!tags?.items?.length && (
                        <div className={element('tags')}>
                            {tags.items.map((tag) => (
                                <span key={tag.tagId}>
                                    <span>{tag.name}</span>
                                </span>
                            ))}
                        </div>
                    )}

                    {formattedAdditionalFields &&
                        formattedAdditionalFields.map((field) =>
                            field.values.map((value, index) => {
                                if (field.type) {
                                    if (!value) {
                                        return null;
                                    }

                                    const fieldLabel = field?.labels?.[index] ? field.labels[index] : value;
                                    // Determine the prefix based on field type
                                    let hrefValue = value;
                                    let icon = mdiLink;

                                    if (field.type === 'phone') {
                                        hrefValue = `tel:${value}`;
                                        icon = mdiPhone;
                                    } else if (field.type === 'email') {
                                        hrefValue = `mailto:${value}`;
                                        icon = mdiEmailOutline;
                                    }

                                    return (
                                        <Link target="_blank" key={field.type + fieldLabel} href={hrefValue}>
                                            <Icon icon={icon} />
                                            {fieldLabel}
                                        </Link>
                                    );
                                }
                                return (
                                    <Text
                                        key={hashCode(String(value))}
                                        className={element('custom-field')}
                                        typography={Typography.body1}
                                        color={theme === Theme.light ? ColorPalette.dark : ColorPalette.light}
                                        colorVariant={ColorVariant.L2}
                                        as="span"
                                    >
                                        {value}
                                    </Text>
                                );
                            }),
                        )}

                    <MetadataLinkList metadataList={metadataList} theme={theme} contentTypeId="" />
                </div>

                {deleteDialogProps && creationDialogProps && (canEdit || canDelete) && (
                    <>
                        <ContextMenu
                            theme={theme}
                            menuOptions={menuOptions}
                            scope={`directory-entry-${directoryEntryId}`}
                        />
                        <DirectoryEntryCreationDialog {...creationDialogProps} />
                        <ConfirmDialog
                            {...deleteDialogProps}
                            confirmMessage={
                                <Text as="p" typography={Typography.body1}>
                                    {deleteDialogProps.confirmMessage}
                                </Text>
                            }
                        />
                    </>
                )}

                {isFavoriteIconVisible && (
                    <div className={element('actions')}>
                        <IconButton
                            emphasis={Emphasis.low}
                            icon={isFavorite ? mdiStar : mdiStarOutline}
                            onClick={isFavorite ? unfavorite : favorite}
                            theme={theme}
                            id={isFavorite ? 'remove-favorite-entry' : 'add-favorite-entry'}
                            label={translateAndReplace(
                                isFavorite
                                    ? DIRECTORY_ENTRY.REMOVE_ENTRY_FROM_FAVORITE
                                    : DIRECTORY_ENTRY.FAVORITE_BUTTON_ARIA,
                                { NAME: title || link || '' },
                            )}
                            aria-pressed={isFavorite}
                        />
                    </div>
                )}
            </div>
            {resourceId && (
                <MicroAppDialog isOpen={isMicroAppDialogOpen} onClose={toggleMicroAppDialog} id={resourceId} />
            )}
        </>
    );
};

DetailedDirectoryEntryBlock.displayName = 'DetailedDirectoryEntryBlock';
